import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { StructuredIngestView } from '@/components/structured-ingest/StructuredIngestView'
import { ApiError } from '@/services/api'
import {
  isProjecting,
  type Preview,
  type Proposal,
  type RowsQuery,
} from '@/services/api/structuredIngest'
import {
  useAnalyzeStructuredDocument,
  useExplainRow,
  usePreviewTable,
  useProjectTable,
  useReannotateTable,
  useStructuredIngestAnalysis,
  useStructuredIngestList,
  useStructuredIngestReadiness,
  useTableRows,
} from './hooks/use-structured-ingest'
import { SchemaRequiredCard } from './SchemaRequiredCard'
import { useRowsSelection } from './useRowsSelection'

/** Edits arrive per keystroke; the preview is recomputed once the investigator pauses. */
const PREVIEW_DEBOUNCE_MS = 600
const ROWS_PAGE = 100

function describe(error: unknown): string {
  if (error instanceof ApiError) return `${error.status}: ${error.message}`
  return error instanceof Error ? error.message : String(error)
}

interface RowsTarget {
  tableId: string
  query: RowsQuery
  title: string
  subtitle?: string
}

export function StructuredIngestPage({ caseId }: { caseId: string }) {
  const readiness = useStructuredIngestReadiness(caseId)
  if (readiness.isPending) return null
  if (readiness.error)
    return (
      <p role="alert" className="p-5 text-destructive">
        {describe(readiness.error)}
      </p>
    )
  if (!readiness.data?.ready) return <SchemaRequiredCard caseId={caseId} />
  return <ReadyStructuredIngestPage caseId={caseId} />
}

function ReadyStructuredIngestPage({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  // `?analysis=<id>` — the deep link from a document's "Review mapping". It seeds the
  // selection synchronously so the "open the newest analysis" default below never
  // races it (with a cached list both effects fire in the same commit).
  const [searchParams, setSearchParams] = useSearchParams()
  const [analysisId, setAnalysisId] = useState<string | null>(() => searchParams.get('analysis'))
  const [recipes, setRecipes] = useState<Record<string, Proposal>>({})
  const [previews, setPreviews] = useState<Record<string, Preview>>({})
  const [previewing, setPreviewing] = useState<string | null>(null)
  const [rowsTarget, setRowsTarget] = useState<RowsTarget | null>(null)
  // The row lens follows the recipe as it stood when the investigator last paused
  // (same debounce as the preview), so a keystroke does not fire a row explanation.
  const [settled, setSettled] = useState<Record<string, Proposal>>({})
  const [lens, setLens] = useState<{ tableId: string; position: number } | null>(null)
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const previewRequests = useRef<Record<string, number>>({})
  const activeAnalysisId = useRef(analysisId)
  activeAnalysisId.current = analysisId

  const listQuery = useStructuredIngestList(caseId)
  const analysisQuery = useStructuredIngestAnalysis(caseId, analysisId)
  const navigate = useNavigate()
  const reannotate = useReannotateTable(caseId)
  const redoFile = useAnalyzeStructuredDocument(caseId)
  const project = useProjectTable(caseId)
  const previewMutation = usePreviewTable(caseId)
  const rowsQuery = useTableRows(
    caseId,
    analysisId ?? '',
    rowsTarget?.tableId ?? '',
    rowsTarget ? { ...rowsTarget.query, limit: ROWS_PAGE } : null
  )
  const analysis = analysisQuery.data ?? null
  const rowsSelection = useRowsSelection(
    caseId,
    analysisId && rowsTarget ? `${analysisId}/${rowsTarget.tableId}` : null
  )
  const lensTable = lens ? analysis?.tables.find(t => t.tableId === lens.tableId) : undefined
  const lensRecipe = lens
    ? (settled[lens.tableId] ??
      recipes[lens.tableId] ??
      lensTable?.reviewRecipe ??
      lensTable?.confirmed?.recipe ??
      lensTable?.proposal ??
      null)
    : null
  const explainQuery = useExplainRow(
    caseId,
    analysisId ?? '',
    lens?.tableId ?? '',
    lensRecipe,
    lens?.position ?? 0
  )

  // Fingerprints of the proposals already loaded into `recipes` (see the effect below).
  const seen = useRef<Record<string, string>>({})

  // Switching analyses resets everything keyed by tableId (`t1`, `t2`, … repeat across
  // analyses, so a stale recipe would otherwise be applied to the wrong table).
  const openAnalysis = useCallback((id: string) => {
    for (const timer of Object.values(timers.current)) clearTimeout(timer)
    for (const key of Object.keys(previewRequests.current)) previewRequests.current[key] += 1
    setPreviewing(null)
    setAnalysisId(id)
    seen.current = {}
    setRecipes({})
    setPreviews({})
    setRowsTarget(null)
    setSettled({})
    setLens(null)
  }, [])

  // The deep link is applied once and removed, so back/refresh do not re-open it (same
  // pattern as the comms page). Also covers a new `?analysis=` while already mounted.
  useEffect(() => {
    const wanted = searchParams.get('analysis')
    if (!wanted) return
    openAnalysis(wanted)
    searchParams.delete('analysis')
    setSearchParams(searchParams, { replace: true })
  }, [searchParams, setSearchParams, openAnalysis])

  // Open the newest analysis of the case by default (uploads elsewhere land here too).
  useEffect(() => {
    if (searchParams.get('analysis')) return // a deep link is pending — it wins
    if (!analysisId && listQuery.data?.length) setAnalysisId(listQuery.data[0].analysisId)
  }, [analysisId, listQuery.data, searchParams])

  // A fresh proposal (upload or re-annotation) replaces the edited recipe for that table;
  // once a projection ran, the confirmed recipe (with the investigator's grains, allowed
  // types, null tokens …) is the starting point. Conversation revisions identify fresh
  // responses even when the model returns the same recipe or takes the same time.
  useEffect(() => {
    if (!analysis) return
    for (const table of analysis.tables) {
      if (table.status !== 'ready') continue
      const recipe = table.reviewRecipe ?? table.confirmed?.recipe ?? table.proposal
      const fingerprint = `${analysis.schemaInfo?.schemaId}:${table.mappingRevision ?? ''}:${JSON.stringify(recipe)}`
      if (seen.current[table.tableId] === fingerprint) continue
      seen.current[table.tableId] = fingerprint
      setRecipes(prev => ({ ...prev, [table.tableId]: recipe }))
      setSettled(prev => ({ ...prev, [table.tableId]: recipe }))
      setPreviews(prev => {
        const { [table.tableId]: _stale, ...rest } = prev
        return rest
      })
    }
  }, [analysis])

  // Preview numbers follow the edited recipe: debounced pandas recompute, no model call.
  const schedulePreview = useCallback(
    (tableId: string, recipe: Proposal) => {
      if (!analysisId) return
      clearTimeout(timers.current[tableId])
      const request = (previewRequests.current[tableId] ?? 0) + 1
      previewRequests.current[tableId] = request
      const isCurrent = () =>
        activeAnalysisId.current === analysisId && previewRequests.current[tableId] === request
      setPreviewing(tableId)
      timers.current[tableId] = setTimeout(() => {
        if (!isCurrent()) return
        setSettled(prev => ({ ...prev, [tableId]: recipe }))
        setPreviewing(tableId)
        previewMutation.mutate(
          { analysisId, tableId, recipe },
          {
            onSuccess: preview => {
              if (!isCurrent()) return
              setPreviews(current => ({ ...current, [tableId]: preview }))
              setRecipes(prev => ({ ...prev, [tableId]: preview.normalized_recipe ?? recipe }))
            },
            onError: error => {
              if (isCurrent()) toast.error(describe(error))
            },
            onSettled: () => {
              if (isCurrent()) setPreviewing(current => (current === tableId ? null : current))
            },
          }
        )
      }, PREVIEW_DEBOUNCE_MS)
    },
    [analysisId, previewMutation]
  )
  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const timer of Object.values(pending)) clearTimeout(timer)
    }
  }, [])

  return (
    <StructuredIngestView
      state={{
        analysis,
        loading: listQuery.isPending || (!!analysisId && analysisQuery.isPending),
        loadError:
          analysisQuery.error || listQuery.error
            ? describe(analysisQuery.error || listQuery.error)
            : null,
        analyses: listQuery.data ?? [],
        recipes,
        previews,
        previewing,
        projecting: project.isPending
          ? (project.variables?.tableId ?? null)
          : (analysis?.tables.find(isProjecting)?.tableId ?? null),
        reannotating:
          reannotate.isPending && reannotate.variables?.analysisId === analysisId
            ? reannotate.variables.tableId
            : null,
        redoingFile: redoFile.isPending,
        rows: {
          open: rowsTarget !== null,
          title: rowsTarget?.title ?? '',
          subtitle: rowsTarget?.subtitle ?? null,
          page: rowsQuery.data ?? null,
          loading: rowsQuery.isFetching,
          error: rowsQuery.error ? describe(rowsQuery.error) : null,
          selection: rowsSelection,
        },
        lens: {
          tableId: lens?.tableId ?? null,
          position: lens?.position ?? 0,
          explanation: explainQuery.data ?? null,
          loading: explainQuery.isFetching,
        },
      }}
      actions={{
        onOpenDocuments: documentId =>
          navigate(
            documentId
              ? `/cases/${caseId}/documents?doc=${encodeURIComponent(documentId)}`
              : `/cases/${caseId}/documents`
          ),
        onOpenAnalysis: openAnalysis,
        onRetry: () => {
          void analysisQuery.refetch()
          void listQuery.refetch()
        },
        onRecipeChange: (tableId, recipe) => {
          setRecipes(prev => ({ ...prev, [tableId]: recipe }))
          schedulePreview(tableId, recipe)
        },
        onReannotate: async (tableId, instructions) => {
          if (!analysisId || reannotate.isPending) return false
          clearTimeout(timers.current[tableId])
          previewRequests.current[tableId] = (previewRequests.current[tableId] ?? 0) + 1
          setPreviewing(null)
          setLens(null)
          try {
            await reannotate.mutateAsync({
              analysisId,
              tableId,
              instructions,
              recipe: recipes[tableId],
            })
            return true
          } catch (error) {
            toast.error(describe(error))
            return false
          }
        },
        onRedoFile: async instructions => {
          const documentId = analysis?.documentId
          if (!documentId || redoFile.isPending) return false
          try {
            const next = await redoFile.mutateAsync({ documentId, instructions })
            openAnalysis(next.analysisId)
            toast.success(t('tableMapping.toast.redone', { file: next.filename }))
            return true
          } catch (error) {
            toast.error(describe(error))
            return false
          }
        },
        onProject: tableId => {
          if (!analysisId) return
          const recipe = recipes[tableId]
          if (!recipe) return
          project.mutate(
            { analysisId, tableId, recipe },
            {
              onSuccess: () => {
                toast.success(t('tableMapping.toast.projected'))
              },
              onError: error => toast.error(describe(error)),
            }
          )
        },
        onShowRows: (tableId, query, title, subtitle) =>
          setRowsTarget({ tableId, query: { ...query, offset: 0 }, title, subtitle }),
        onRowsPage: offset =>
          setRowsTarget(current =>
            current ? { ...current, query: { ...current.query, offset } } : current
          ),
        onCloseRows: () => setRowsTarget(null),
        onLensChange: (tableId, position) => setLens(tableId ? { tableId, position } : null),
      }}
    />
  )
}
